import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { DashList, Formula, LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.como-hacer-inventario-hosteleria.${field}`

const PGC_HREF = 'https://www.boe.es/buscar/act.php?id=BOE-A-2007-19884'

const SOURCES: { href?: string; labelKey: string; fallback: string }[] = [
    {
        href: PGC_HREF,
        labelKey: 'sources.1',
        fallback:
            'Real Decreto 1514/2007, de 16 de noviembre, por el que se aprueba el Plan General de Contabilidad, norma de registro y valoración 10.ª, existencias.',
    },
    {
        labelKey: 'sources.2',
        fallback:
            'Caso de cliente: asesoría de Tipi, contado de forma anónima. Los porcentajes de food cost son los del cliente.',
    },
    {
        labelKey: 'sources.3',
        fallback: 'Método de conteo, venta supuesta y paso a euros: elaboración propia de Tipi.',
    },
]

export function ComoHacerInventarioHosteleriaArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'pediste-menos', label: t(k('toc.1'), '1. Pediste menos y el food cost no se movió') },
        { id: 'inventario-teorico', label: t(k('toc.2'), '2. Inventario teórico') },
        { id: 'elegir', label: t(k('toc.3'), '3. Elige qué contar por euros') },
        { id: 'contar', label: t(k('toc.4'), '4. Cuenta el último día del mes') },
        { id: 'precio', label: t(k('toc.5'), '5. ¿A qué precio valoro el stock?') },
        { id: 'comparar', label: t(k('toc.6'), '6. Compara: la diferencia es merma') },
        { id: 'cada-cuanto', label: t(k('toc.7'), '7. ¿Cada cuánto hacer inventario?') },
        { id: 'caso', label: t(k('toc.8'), '8. Del 46 % al 42 %') },
        { id: 'limites', label: t(k('toc.9'), '9. Lo que 25 productos no te cuentan') },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Para cerrar un mes real hacen falta dos conteos: el último día del mes anterior y el último día de este. No hay que contarlo todo: las 20 o 30 referencias que más pesan en euros suelen sumar cerca del 80 % del valor del almacén. Se valoran sin IVA y al precio del último albarán. Y la diferencia entre lo que debería haber y lo que cuentas es la merma que hoy no ves. Así se hace un inventario en hostelería cuando no tienes una tarde libre.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Cocinero contando productos con una lista en la cámara frigorífica de un restaurante',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGap2xl, 'text-article-muted-soft')}>
                                {t(k('published'), 'Publicado el 13 de octubre de 2026.')}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='pediste-menos' className={articleUi.h2}>
                        {t(k('h2.pedisteMenos'), 'Pediste menos durante tres meses y el food cost no se movió')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.pediste.1Before'), 'Tu food cost estaba en el ')}
                            <strong>{t(k('p.pediste.1Strong'), '46 %')}</strong>
                            {t(
                                k('p.pediste.1After'),
                                ' y tenías una explicación: la cámara llena.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.pediste.2'),
                                'Comprabas de más, el dinero se quedaba parado en cajas y eso inflaba el número. Así que empezaste a pedir justo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.pediste.3'), 'Tres meses después, el número seguía ahí.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.pediste.4'),
                                'Es el caso de un cliente nuestro, y la creencia era razonable. El problema es que nunca la había comprobado, porque nunca había contado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.pediste.em'),
                                    'De nada sirve cerrar el mes si no sabes cuánto dinero hay en tu cámara.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.pediste.5'),
                                'Y un aviso antes de seguir: contar no te baja el food cost. Te dice cuál es, y casi siempre es peor de lo que creías.',
                            )}
                        </p>
                    </div>

                    <h2 id='inventario-teorico' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(
                            k('h2.teorico'),
                            'Lo que debería quedar en la cámara tiene nombre: inventario teórico',
                        )}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.teorico.1'),
                                'El food cost real de un mes no sale de lo que compras. Sale de lo que consumes.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.teorico.formulaLead'),
                                    'Consumo del mes = lo que había al empezar + lo que compraste − lo que queda al terminar.',
                                )}
                            </span>
                        </p>
                        <Formula>
                            {t(
                                k('formula.consumo'),
                                'Consumo = Inventario inicial + Compras − Inventario final',
                            )}
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.teorico.2'),
                                'Las compras te las dan los albaranes. Lo que queda solo lo sabes contando.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.teorico.3'),
                                'El inventario teórico es lo que debería haber en tu almacén si todo lo que salió se hubiera vendido según la receta: lo que había, más lo que compraste, menos tus ventas multiplicadas por los gramos de cada escandallo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.teorico.4Before'),
                                'Si no tienes escandallados los platos que gastan una referencia, esa referencia no tiene teórico. Por eso conviene entender antes ',
                            )}
                            <LearnArticleLink slug='coste-teorico-vs-coste-real-cocina'>
                                {t(
                                    k('linkTeoricoReal'),
                                    'por qué un plato cuesta una cosa en la ficha y otra en tu cocina',
                                )}
                            </LearnArticleLink>
                            {t(k('p.teorico.4After'), '.')}
                        </p>
                    </div>

                    <h2 id='paso-a-paso' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.pasoAPaso'), 'Cómo hacer un inventario en hostelería, paso a paso')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.pasoAPaso.lead'),
                                'Cuatro pasos: lista por euros, conteo el último día, valoración al último albarán y comparación con el teórico.',
                            )}
                        </p>
                    </div>

                    <h3 id='elegir' className={cn(articleUi.h3Block, 'scroll-mt-[90px]')}>
                        <span className={articleUi.stepNum}>01</span>{' '}
                        {t(k('h3.elegir'), 'Elige qué contar por euros, no por kilos')}
                    </h3>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.elegir.1'), 'No cuentas todo. Cuentas lo que pesa en dinero.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.elegir.em'),
                                    'En la mayoría de almacenes, el 20 % de las referencias suma cerca del 80 % del valor. Es la regla de Pareto: una regla práctica, no una ley.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.elegir.2'),
                                'Para sacar tu lista, coge los albaranes de los últimos tres meses y ordena las referencias por euros gastados. Las 20 o 30 primeras son tu inventario.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.elegir.3'),
                                'La trampa es elegir por volumen. Veinte kilos de patata ocupan media cámara y valen menos que dos piezas de ibérico. La patata se queda fuera; el ibérico, dentro.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.elegir.4'),
                                'Cuando llevaba las compras de ocho locales, la lista de lo que había que contar la daba yo. Así no cambia según quién cuente.',
                            )}
                        </p>
                    </div>

                    <h3 id='contar' className={cn(articleUi.h3Block, 'scroll-mt-[90px]')}>
                        <span className={articleUi.stepNum}>02</span>{' '}
                        {t(k('h3.contar'), 'Cuenta el último día del mes, siempre a la misma hora')}
                    </h3>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.contar.1'),
                                'Antes de abrir o después de cerrar, nunca con el servicio en marcha. Y a la misma hora cada mes, para que los dos conteos se puedan comparar.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.contar.2'),
                                'Cuenta también lo abierto: la botella empezada, el queso cortado, la mise en place de la cámara. Cada elaboración se valora con su propio escandallo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.contar.em'),
                                    'La trampa más cara es el albarán que falta. Si el pedido entró el día 31 y está en la cámara, su albarán tiene que estar en las compras de este mes.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.contar.3'),
                                'Si no está, cuentas como existencias algo que no figura en tus compras, y el food cost del mes sale más bajo de lo que es.',
                            )}
                        </p>
                    </div>

                    <h3 id='precio' className={cn(articleUi.h3Block, 'scroll-mt-[90px]')}>
                        <span className={articleUi.stepNum}>03</span>{' '}
                        {t(k('h3.precio'), '¿A qué precio valoro el stock?')}
                    </h3>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.precio.1'),
                                'Unidades contadas por el precio del último albarán, sin IVA. Siempre con el mismo criterio.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.precio.2'),
                                'Valorar con el precio que recuerdas es valorar con el de hace seis meses.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.precio.emBefore'),
                                    'El precio del último albarán se parece mucho al método FIFO',
                                )}
                            </span>
                            {t(
                                k('p.precio.emAfter'),
                                ', el que da por hecho que lo primero que entra es lo primero que sale. El ',
                            )}
                            <a href={PGC_HREF} target='_blank' rel='noopener noreferrer' className={articleUi.link}>
                                {t(k('linkPgc'), 'Plan General de Contabilidad')}
                            </a>
                            {t(
                                k('p.precio.pgcAfter'),
                                ' fija como método general el precio medio ponderado, pero añade que ',
                            )}
                            <em>
                                {t(
                                    k('p.precio.pgcQuote'),
                                    '"el método FIFO es aceptable y puede adoptarse si la empresa lo considerase más conveniente para su gestión"',
                                )}
                            </em>
                            {t(k('p.precio.pgcEnd'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.precio.3'), 'Para cerrar tu mes, con eso basta.')}
                        </p>
                    </div>

                    <h3 id='comparar' className={cn(articleUi.h3Block, 'scroll-mt-[90px]')}>
                        <span className={articleUi.stepNum}>04</span>{' '}
                        {t(k('h3.comparar'), 'Compara: la diferencia es lo que salió sin cobrarse')}
                    </h3>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.comparar.1'),
                                'Pon al lado, referencia por referencia, lo que debería haber y lo que has contado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.comparar.em'),
                                    'La diferencia en euros es lo que salió de tu almacén sin pasar por caja.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.comparar.2'),
                                'No todo es merma. Ahí entran el género caducado y el mal porcionado, pero también las invitaciones, la comida de personal sin apuntar y los gramajes que en el pase nadie respeta.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.comparar.3'),
                                'Antes de buscar culpables, revisa el escandallo de los platos que gastan esa referencia. Es lo primero que se corrige y lo que menos cuesta.',
                            )}
                        </p>
                    </div>

                    <h2 id='cada-cuanto' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.cadaCuanto'), '¿Cada cuánto hay que hacer inventario en un restaurante?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.cadaCuanto.em'),
                                    'Una vez al mes, el último día. Es la cadencia que te da un food cost mensual real y la que se puede sostener.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cadaCuanto.1'),
                                'El primer mes no compara nada. Solo te da el punto de partida: las existencias iniciales del segundo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cadaCuanto.2'),
                                'Entre medias, auditorías sin fecha fija: cinco referencias de la lista, a mitad de mes y sin avisar. Es lo que yo hacía, y sirve para ver si el teórico aguanta antes de que acabe el mes.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.cadaCuanto.3'),
                                'Si tu local es pequeño, el método no cambia. Cambia la lista: con 10 o 15 referencias probablemente tienes la mayor parte del valor, y el conteo cabe antes de abrir.',
                            )}
                        </p>
                    </div>

                    <h2 id='caso' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.caso'), 'Del 46 % al 42 %, y no al 35 %')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caso.1'), 'Vuelve al cliente del principio.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caso.2Before'), 'Esperaba que, descontando lo que tenía parado en la cámara, su food cost bajase al ')}
                            <strong>{t(k('p.caso.2Strong35'), '35 %')}</strong>
                            {t(k('p.caso.2Mid'), '. Cerró su primer mes con inventario y ')}
                            <strong>{t(k('p.caso.2Strong42'), 'no bajó del 42 %')}</strong>
                            {t(k('p.caso.2After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.caso.3'),
                                'El stock explicaba cuatro puntos como mucho. Los otros siete no estaban en la cámara: se habían ido en merma.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd, 'italic text-article-muted')}>
                            {t(
                                k('p.caso.note'),
                                'Los euros son ilustrativos: suponemos 30.000 € de venta al mes sin IVA. Los % de food cost son del cliente; la venta y el paso a euros, no. No son un teórico–físico medido referencia a referencia.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caso.4Before'), 'Con esa venta, el 46 % eran ')}
                            <strong>{t(k('p.caso.4Strong13800'), '13.800 euros')}</strong>
                            {t(k('p.caso.4Mid'), ' en compras y el 42 % real, ')}
                            <strong>{t(k('p.caso.4Strong12600'), '12.600')}</strong>
                            {t(k('p.caso.4After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caso.5Before'), 'Lo que había parado en la cámara eran ')}
                            <strong>{t(k('p.caso.5Strong1200'), '1.200 euros')}</strong>
                            {t(k('p.caso.5Mid'), '. Lo que se fue en merma, ')}
                            <strong>{t(k('p.caso.5Strong2100'), 'al menos 2.100 euros al mes')}</strong>
                            {t(k('p.caso.5After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.caso.em'),
                                    'Son más de 25.000 euros al año que no estaban en ninguna caja. Pedir menos no los iba a recuperar nunca.',
                                )}
                            </span>
                        </p>
                        <div className={cn(articleUi.stats, 'mt-2')}>
                            <div className={articleUi.stat}>
                                <p className={articleUi.statLabel}>{t(k('stat.stock.label'), 'Stock parado (≈ 4 pts)')}</p>
                                <p className={articleUi.statValue}>{t(k('stat.stock.value'), '1.200 €')}</p>
                            </div>
                            <div className={articleUi.statAlert}>
                                <p className={articleUi.statLabelAlert}>{t(k('stat.merma.label'), 'Merma al mes')}</p>
                                <p className={articleUi.statValueAlert}>{t(k('stat.merma.value'), '≥ 2.100 €')}</p>
                            </div>
                            <div className={articleUi.statAccent}>
                                <p className={articleUi.statLabelAccent}>{t(k('stat.year.label'), 'Al año sin cobrar')}</p>
                                <p className={articleUi.statValueAccent}>{t(k('stat.year.value'), '> 25.000 €')}</p>
                            </div>
                        </div>
                        <p className={cn(articleUi.pItalic, 'mt-2')}>
                            {t(
                                k('p.caso.statsNote'),
                                'Ilustración con venta inventada: 4 pts de stock y 7 pts hasta el 35 % esperado ≈ 1.200 € y 2.100 €.',
                            )}
                        </p>
                    </div>

                    <h2 id='limites' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.limites'), 'Lo que 25 productos no te cuentan')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.limites.1'),
                                'El 20 % del valor que queda fuera de la lista no lo controlas. Si una referencia barata empieza a desaparecer, no lo verás hasta que la metas en la lista.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.limites.2'), 'Y tres cosas que conviene no dar por ciertas:')}
                        </p>
                        <DashList
                            items={[
                                t(
                                    k('list.limites.1'),
                                    'El 80/20 varía según la carta. Con muchos productos caros, la lista se alarga. Revísala cada trimestre con los albaranes.',
                                ),
                                t(
                                    k('list.limites.2'),
                                    'El precio del último albarán sirve para gestionar. No tiene por qué ser el criterio con el que tu gestoría valora las existencias a 31 de diciembre. Eso lo cierra ella.',
                                ),
                                t(
                                    k('list.limites.3'),
                                    'El teórico vale lo que valgan tus escandallos. Si los gramos de la ficha no son los que salen por el pase, parte de la diferencia es del escandallo, no de la merma.',
                                ),
                            ]}
                        />
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.limites.3'),
                                'El caso es de un solo cliente. Tu diferencia puede ser mayor o menor, pero no la vas a conocer sin contar.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.limites.4'),
                                'Si nunca has cerrado un mes contando y no sabes por qué referencias empezar, cuéntanoslo: con tus albaranes, escandallos y el extracto del TPV te ayudamos a sacar el teórico y a fijar las 20 o 30 referencias a contar.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.limites.5'),
                                'El día 31 cuentas. El día 1 ya sabes cuánto dinero tienes en la cámara.',
                            )}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Nunca has cerrado un mes contando?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Cuéntanos cómo lo llevas: con tus albaranes, escandallos y el extracto del TPV te ayudamos a sacar el teórico y a fijar las 20 o 30 referencias a contar.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                    <p className={cn(articleUi.ctaClose, 'mt-6')}>
                        {t(k('cta.closeBefore'), 'Desde ')}
                        <TipiLink />
                        {t(
                            k('cta.closeAfter'),
                            ' te ayudamos a ver la merma en euros, no solo en porcentaje.',
                        )}
                    </p>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.labelKey}>
                                {source.href ? (
                                    <a
                                        href={source.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={articleUi.link}
                                    >
                                        {t(k(source.labelKey), source.fallback)}
                                    </a>
                                ) : (
                                    t(k(source.labelKey), source.fallback)
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
